import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import { useState, useEffect } from 'react'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { useLazyQuery, useMutation } from '@apollo/client'
import { ADD_BOAT_ROUTE_ASSIGNMENT, UPDATE_BOAT_ROUTE_ASSIGNMENT } from 'graphql/route/mutations'
import { GET_BOAT_ROUTE_ASSIGNMENT } from 'graphql/route/queries'
import { BoatNameDropdown } from 'components/custom/BoatNameDropdown'
import { CommonStatusDropdown } from 'components/custom/commonStatusDropdown'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/material_blue.css'
export default function BoatRouteAssignmentForm() {
    const { route_id, id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Adding Boat'))
    const [submitLoading, setSubmitLoading] = useState(false)
    const [assignedBoat, setAssignedBoat] = useState(null)

    useEffect(() => {
        if (id) {
            if (id != 0) getAssignedBoatById()
        }
    }, [id])

    const handleBack = () => {
        navigate(`/metro-routes/metro-route-details/${route_id}`,
            { state: { navBackTab: 'assignboat' } }
        )
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    // Mutation Add Captain Shift Schedule
    const [addAssignBoat] = useMutation(ADD_BOAT_ROUTE_ASSIGNMENT, {
        onCompleted: data => {
            if (data.addBoatRouteAssignment.code === '200' && data.addBoatRouteAssignment.success) {
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                setSubmitLoading(false)
                setAddError(data.addBoatRouteAssignment.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    //Mutation Update Captain Shift Schedule
    const [updateAssignBoat] = useMutation(UPDATE_BOAT_ROUTE_ASSIGNMENT, {
        onCompleted: data => {
            if (data.updateBoatRouteAssignment.success) {
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                setAddError(data.updateBoatRouteAssignment.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    // Submit Shift Schedule
    const submitAssignBoat = values => {
        if (id != 0) {
            updateAssignBoat({
                variables: {
                    assignmentId: id,
                    startDate: values.startDate,
                    endDate: values.endDate,
                    statusCode: values.status || 'ACT',
                },
            })
        } else {
            console.log('Values:====', values)
            addAssignBoat({
                variables: {
                    boatRouteAssignment: {
                        boat_id: values.boatId,
                        route_id: route_id,
                        start_date: values.startDate,
                        end_date: values.endDate || null,
                        status_code: values.status || 'ACT',
                    },
                },
            })
        }
    }

    // For Edit Schedule
    const [getAssignedBoat, { loading: getListLoading, error: getListError }] = useLazyQuery(
        GET_BOAT_ROUTE_ASSIGNMENT,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                const get_response = data.getBoatRouteAssignment.route_assignments[0]
                setAssignedBoat(get_response)
                console.log('Assigned Boat:', get_response)
            },
        }
    )
    const getAssignedBoatById = () => {
        getAssignedBoat({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                assignmentFilter: {
                    assignment_id: id,
                    boat_id: null,
                    route_id: route_id,
                    start_date: null,
                    end_date: null,
                    status_code: null,
                },
            },
        })
    }

    // Form Validation
    const assignBoatValidationForm = useFormik({
        enableReinitialize: true,
        initialValues: {
            boatId: assignedBoat?.boat.id || '',
            status: assignedBoat?.status.code || '',
            startDate: assignedBoat?.start_date || '',
            endDate: assignedBoat?.end_date || '',
        },
        validationSchema: Yup.object({
            boatId: Yup.string().required(t('Boat Required')),
            status: Yup.string().required(t(' Status Required')),
            startDate: Yup.string().required(t('Start Date Required')),
        }),
        onSubmit: values => {
            console.log('Form Data:', values)
            setSubmitLoading(true)
            submitAssignBoat(values)
        },
    })

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <div></div>
                <div className="flex gap-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={submitLoading}
                        onClick={() => {
                            assignBoatValidationForm.handleSubmit()
                        }}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>
            <form>
                <div className="space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Assign Boats to Route')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {id != 0 ? 'Edit' : 'Add'} {t('Boat Assignment')} {t('to Route')}
                            </p>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-full">
                                <label htmlFor="captain" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Boat')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <BoatNameDropdown
                                        fields={assignBoatValidationForm.getFieldProps('boatId')}
                                        purpose="add"
                                        statusCode="ACT"
                                    />
                                    {assignBoatValidationForm.touched.boatId &&
                                        assignBoatValidationForm.errors.boatId && (
                                            <p className="text-error text-xs mt-1">
                                                {assignBoatValidationForm.errors.boatId}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div className="sm:col-span-full">
                                <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Status')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <CommonStatusDropdown
                                        statusFor="BoatRouteAssignmentStatus"
                                        fields={assignBoatValidationForm.getFieldProps('status')}
                                        purpose="add"
                                    />
                                    {assignBoatValidationForm.touched.status &&
                                        assignBoatValidationForm.errors.status && (
                                            <p className="text-error text-xs mt-1">
                                                {assignBoatValidationForm.errors.status}
                                            </p>
                                        )}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="from-date"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Start From')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        id="startDate"
                                        name="startDate"
                                        placeholder="yyyy/mm/dd"
                                        value={assignBoatValidationForm.values.startDate}
                                        onChange={date => assignBoatValidationForm.setFieldValue('startDate', date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '')}
                                        options={{

                                            maxDate: "2050-12-31",
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            dateFormat: 'Y/m/d',
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                    />
                                    {assignBoatValidationForm.touched.startDate && assignBoatValidationForm.errors.startDate && (
                                        <p className="text-error text-xs mt-1">{assignBoatValidationForm.errors.startDate}</p>
                                    )}

                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="till-date"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('End Date')}
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        id="endDate"
                                        name="endDate"
                                        placeholder='yyyy/mm/dd'
                                        value={assignBoatValidationForm.values.endDate}
                                        onChange={date => assignBoatValidationForm.setFieldValue('endDate', date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '')}
                                        options={{
                                            altFormat: 'Y/m/d',
                                            dateFormat: 'Y/m/d',
                                            maxDate: "2050-12-31",
                                            minDate: assignBoatValidationForm.values.startDate,
                                            altInput: true,
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                    />
                                    {assignBoatValidationForm.touched.endDate && assignBoatValidationForm.errors.endDate && (
                                        <p className="text-error text-xs mt-1">{assignBoatValidationForm.errors.endDate}</p>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={submitLoading}
                        onClick={() => {
                            console.log('Form Data:', assignBoatValidationForm)
                            assignBoatValidationForm.handleSubmit()
                        }}
                        type="button"
                        className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                        {t('Save')}
                    </button>
                </div>
            </form>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={addError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
